import { useEffect, useMemo, useState, type ChangeEvent } from "react";
import RightTimeApi from "@/API";
import "@style/Admin.scss";
import Enums from "@/enums";
import { toast } from "react-toastify";
import Modal from "@modalSrc/Modal";
import ImagePickerModal from "@modals/ImagePickerModal";
import StringInputModal from "@elements/modals/StringInputModal";
import ConfirmModal from "@modals/ConfirmModal";
import { MdCheckBoxOutlineBlank, MdCheckBox } from "react-icons/md";
import { FaPlusSquare } from "react-icons/fa";
import { IoTrashOutline } from "react-icons/io5";
import {
    enrichCompanyWithBillingOrgsAsync,
    hasSystemAdminPermission,
    saveBillingOrgAsync,
    type BillingOrgOption,
} from "@/utils/billingOrgHelpers";
import {
    loadBillingSettingsAsync,
    saveBillingSettingsAsync,
} from "@/utils/billingSettingsHelpers";

type Permission = { companyId?: number; permissionId?: number };
type RoleOption = { key?: number | string; value?: string };
type StatusOption = { ctsId?: number | string; display?: string };

type CompanyData = {
    id?: number;
    name?: string;
    alias?: string;
    active?: boolean;
    isBillable?: boolean;
    defaultUserRoleId?: number;
    subEntityTypeName?: string;
    timedOutStatusId?: number;
    billingOrgId?: number;
    billingOrgName?: string;
    billingOrgs?: BillingOrgOption[];
    billingEmail?: string;
    imageId?: number;
    permissions?: Permission[];
    roles?: RoleOption[];
    statuses?: StatusOption[];
    departmentsEnabled?: boolean;
};

type CompanyDataModalProps = {
    isOpen: boolean;
    onClose?: () => void;
    companyId?: number | string;
    updateSpinner?: (isLoading: boolean) => void;
    adminPerms?: number[];
};

export default function CompanyDataModal({
    isOpen,
    onClose,
    companyId,
    updateSpinner,
    adminPerms,
}: CompanyDataModalProps) {
    const [localCompany, updateLocalCompany] = useState<CompanyData>({});
    const [companyEditable, updateCompanyEditable] = useState(false);
    const [isImageModalOpen, setIsImageModalOpen] = useState(false);
    const [showDuplicateModal, setShowDuplicateModal] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showDeletePasswordModal, setShowDeletePasswordModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [billingOrgSaving, setBillingOrgSaving] = useState(false);
    const [billingSettingsSaving, setBillingSettingsSaving] = useState(false);
    const [companyImageUrl, updateCompanyImageUrl] = useState("");

    useEffect(() => {
        if (isOpen && companyId) {
            setIsImageModalOpen(false);
            setShowDuplicateModal(false);
            getCompanyAsync();
        }
    }, [isOpen, companyId]);

    const canDuplicate = useMemo(() => {
        return (
            localCompany.permissions &&
            localCompany.permissions.some(
                    (p) => p.permissionId === Enums.ROLE_PERMS.SystemAdmin
            )
        );
    }, [localCompany.permissions]);

    const canSave = useMemo(() => {
        return (
            localCompany.permissions &&
            (localCompany.permissions.some(
                (p) => p.companyId === 1 && p.permissionId === Enums.ROLE_PERMS.SystemAdmin
            ) ||
                localCompany.permissions.some(
                    (p) => p.companyId === localCompany.id && p.permissionId === Enums.ROLE_PERMS.EditCompany
                ))
        );
    }, [localCompany.permissions, localCompany.id]);

    const isProdEnvironment = (import.meta.env.VITE_ENV || "PROD") === "PROD";

    const canDelete = useMemo(() => {
        return !!localCompany.permissions?.some(
            (p) => p.companyId === 1 && p.permissionId === Enums.ROLE_PERMS.SystemAdmin
        );
    }, [localCompany.permissions]);

    const isSystemAdmin = useMemo(
        () => hasSystemAdminPermission(localCompany.permissions, adminPerms),
        [localCompany.permissions, adminPerms]
    );

    const canManageBilling = useMemo(() => {
        if (isSystemAdmin) return true;
        return !!localCompany.permissions?.some(
            (p) => p.companyId === localCompany.id && p.permissionId === Enums.ROLE_PERMS.CompanyMenuBilling
        );
    }, [isSystemAdmin, localCompany.permissions, localCompany.id]);

    const billingOrgOptions = useMemo(
        () => localCompany.billingOrgs ?? [],
        [localCompany.billingOrgs]
    );

    if (!isOpen) return null;

    async function getCompanyAsync() {
        if (!companyId) return;
        if (updateSpinner) updateSpinner(true);
        const resp = await RightTimeApi.getAsync("/CompanyInfo2/" + companyId);
        if (updateSpinner) updateSpinner(false);
        if (resp && resp.ok && resp.jsonData) {
            const data = await enrichCompanyWithBillingOrgsAsync(resp.jsonData, adminPerms);
            updateLocalCompany(data);
            if (data.imageId) {
                let url = RightTimeApi.URL("/Image/" + data.imageId);
                updateCompanyImageUrl(url);
            } else {
                updateCompanyImageUrl("");
            }
            if (data.permissions && data.permissions.length > 0) {
                const canEdit =
                    data.permissions.some(
                        (p) => p.companyId === data.id && p.permissionId === Enums.ROLE_PERMS.EditCompany
                    ) || hasSystemAdminPermission(data.permissions, adminPerms);
                updateCompanyEditable(canEdit);
            } else {
                updateCompanyEditable(false);
            }

            if (data.id && (hasSystemAdminPermission(data.permissions, adminPerms) || data.permissions?.some(
                (p) => p.companyId === data.id && p.permissionId === Enums.ROLE_PERMS.CompanyMenuBilling
            ))) {
                const settings = await loadBillingSettingsAsync(data.id);
                if (settings) {
                    updateLocalCompany((prev) => ({
                        ...prev,
                        billingEmail: settings.billingEmail || "",
                    }));
                }
            }
        }
    }

    async function saveBillingEmailAsync(billingEmail: string) {
        if (!localCompany.id || !canManageBilling) return;

        updateLocalCompany({ ...localCompany, billingEmail });
        setBillingSettingsSaving(true);
        if (updateSpinner) updateSpinner(true);
        try {
            const resp = await saveBillingSettingsAsync(localCompany.id, { billingEmail });
            if (!resp?.ok) {
                toast.error("Unable to update billing settings.");
            }
        } catch {
            toast.error("Unable to update billing settings.");
        } finally {
            setBillingSettingsSaving(false);
            if (updateSpinner) updateSpinner(false);
        }
    }

    async function onBillingEmailBlur(e: ChangeEvent<HTMLInputElement>) {
        await saveBillingEmailAsync(e.target.value);
    }

    async function onSaveCompanyAsync() {
        var updatedData = {
            id: localCompany.id,
            name: localCompany.name,
            alias: localCompany.alias,
            active: localCompany.active,
            isBillable: localCompany.isBillable,
            departmentsEnabled: localCompany.departmentsEnabled,
            defaultRoleId: localCompany.defaultUserRoleId,
            subEntityTypeName: localCompany.subEntityTypeName,
            timedOutStatusId: localCompany.timedOutStatusId,
            imageId: localCompany.imageId,
        };

        const resp = await RightTimeApi.sendAsync("/Company", updatedData);
        if (resp && resp.ok) {
            toast.success("Company updated.");
            if (onClose) onClose();
        }
    }

    function onChange(e) {
        const target = e.target as HTMLInputElement | HTMLSelectElement | HTMLElement;
        const currentTarget = e.currentTarget as HTMLElement;
        const valueInt = parseInt((target as HTMLInputElement | HTMLSelectElement).value || "", 10);
        let name = target.getAttribute("name");
        if (!name) name = currentTarget.getAttribute("data-name");
        switch (name) {
            case "Name":
                updateLocalCompany({ ...localCompany, name: (target as HTMLInputElement).value });
                break;
            case "Alias":
                updateLocalCompany({ ...localCompany, alias: (target as HTMLInputElement).value });
                break;
            case "SubEntityTypeName":
                updateLocalCompany({ ...localCompany, subEntityTypeName: (target as HTMLInputElement).value });
                break;
            case "DefaultUserRoleId":
                updateLocalCompany({ ...localCompany, defaultUserRoleId: valueInt });
                break;
            case "TimedOutStatusId":
                updateLocalCompany({ ...localCompany, timedOutStatusId: valueInt });
                break;
            case "Active":
                updateLocalCompany({ ...localCompany, active: !localCompany.active });
                break;
            case "IsBillable":
                updateLocalCompany({ ...localCompany, isBillable: !localCompany.isBillable });
                break;
            case "DepartmentsEnabled":
                updateLocalCompany({ ...localCompany, departmentsEnabled: !localCompany.departmentsEnabled });
                break;
            default:
                break;
        }
    }

    async function onBillingOrgChange(e: ChangeEvent<HTMLSelectElement>) {
        if (!localCompany.id || !isSystemAdmin) return;

        const billingOrgId = parseInt(e.target.value, 10);
        if (!billingOrgId || billingOrgId === localCompany.billingOrgId) return;

        const previousId = localCompany.billingOrgId;
        const previousName = localCompany.billingOrgName;
        const orgName =
            localCompany.billingOrgs?.find((o) => Number(o.key) === billingOrgId)?.value ?? "";

        updateLocalCompany({ ...localCompany, billingOrgId, billingOrgName: orgName });
        setBillingOrgSaving(true);
        if (updateSpinner) updateSpinner(true);

        try {
            const resp = await saveBillingOrgAsync(localCompany.id, billingOrgId);
            if (resp?.ok) {
                const savedName = resp.jsonData?.billingOrgName;
                if (savedName) {
                    updateLocalCompany((prev) => ({ ...prev, billingOrgName: savedName }));
                }
                toast.success("Billing org updated.");
            } else {
                updateLocalCompany((prev) => ({
                    ...prev,
                    billingOrgId: previousId,
                    billingOrgName: previousName,
                }));
                toast.error("Unable to update billing org.");
            }
        } finally {
            setBillingOrgSaving(false);
            if (updateSpinner) updateSpinner(false);
        }
    }

    async function onDuplicateCompanyAsync(newCompanyName) {
        if (updateSpinner) updateSpinner(true);
        const response = await RightTimeApi.postAsync(
            "/Company/" + localCompany.id + "/Duplicate",
            { name: newCompanyName }
        );
        if (updateSpinner) updateSpinner(false);
        if (response.ok) {
            let data = response.jsonData;
            toast.success("Company duplicated.");
            window.location.href = "/Company/" + data.companyId + "?tab=company";
        }
    }

    function handleDuplicateSubmit(newCompanyName) {
        setShowDuplicateModal(false);
        onDuplicateCompanyAsync(newCompanyName);
    }

    function handleDeleteConfirmed() {
        setShowDeleteConfirm(false);
        if (isProdEnvironment) {
            setShowDeletePasswordModal(true);
            return;
        }
        void onDeleteCompanyAsync();
    }

    function handleDeletePasswordCancel() {
        setShowDeletePasswordModal(false);
    }

    function handleDeletePasswordSubmit(adminPassword: string) {
        void onDeleteCompanyAsync(adminPassword);
    }

    async function onDeleteCompanyAsync(adminPassword?: string) {
        if (!localCompany?.id || !canDelete || isDeleting) return;
        setIsDeleting(true);
        if (updateSpinner) updateSpinner(true);
        let keepPasswordModalOpen = false;
        try {
            const payload = adminPassword ? { adminPassword } : undefined;
            const resp = await RightTimeApi.deleteAsync(`/Company/${localCompany.id}`, payload);
            if (resp && resp.ok) {
                toast.success("Company deleted.");
                window.location.href = "/Admin?tab=companies";
                return;
            }
            if (resp?.status === 403 && isProdEnvironment) {
                toast.error("Invalid admin password.");
                keepPasswordModalOpen = true;
                return;
            }
            toast.error("Unable to delete company.");
        } finally {
            if (updateSpinner) updateSpinner(false);
            setIsDeleting(false);
            setShowDeleteConfirm(false);
            if (!keepPasswordModalOpen) {
                setShowDeletePasswordModal(false);
            }
        }
    }


    return (
        <>
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                title={`Company Data${localCompany?.name ? ` - ${localCompany.name}` : ""}`}
                size="lg"
                className='companyDataModal'
                footer={
                    <>
                        <div className="footerLeft">
                            {canDuplicate ? (
                                <button className="btn btn-secondary" onClick={() => setShowDuplicateModal(true)}>Duplicate</button>
                            ) : null}
                            {canDelete ? (
                                <button
                                    type="button"
                                    className="btn btn-danger iconButton"
                                    onClick={() => setShowDeleteConfirm(true)}
                                    disabled={isDeleting || !localCompany?.id}
                                    title={isDeleting ? "Deleting..." : "Delete Company"}
                                >
                                    <IoTrashOutline />
                                </button>
                            ) : null}
                        </div>
                        <div className="footerRight">
                            {canSave ? (
                                <button className="btn btn-primary okButton" onClick={onSaveCompanyAsync}>Save</button>
                            ) : null}
                        </div>
                    </>
                }
            >
                <div className="tableWrapper dimensionTable">
                    <table className="formTable">
                        <caption>
                          <span className="title">Company Data</span>
                        </caption>
                        <tbody>
                            <tr>
                                <th className="label">Name</th>
                                <td>
                                    <input
                                        value={localCompany.name || ""}
                                        name="Name"
                                        type="text"
                                        className="companyNameInput"
                                        readOnly={!companyEditable}
                                        onChange={onChange}
                                    />
                                </td>
                            </tr>
                            <tr>
                                <th className="label">Alias</th>
                                <td>
                                    <input
                                        value={localCompany.alias || ""}
                                        name="Alias"
                                        type="text"
                                        maxLength={128}
                                        className="companyAliasInput"
                                        readOnly={!companyEditable}
                                        onChange={onChange}
                                    />
                                </td>
                            </tr>
                            <tr>
                                <th className="label">Billing Org</th>
                                <td>
                                    {isSystemAdmin && billingOrgOptions.length > 0 ? (
                                        <select
                                            value={localCompany.billingOrgId || 0}
                                            name="BillingOrgId"
                                            disabled={billingOrgSaving}
                                            onChange={onBillingOrgChange}
                                        >
                                            {billingOrgOptions.map((org, i) => (
                                                <option
                                                    key={"billingOrg" + (org.key !== undefined ? org.key : i)}
                                                    value={org.key}
                                                >
                                                    {org.value}
                                                </option>
                                            ))}
                                        </select>
                                    ) : (
                                        <span>{localCompany.billingOrgName || ""}</span>
                                    )}
                                </td>
                            </tr>
                            <tr>
                                <th className="label">Active</th>
                                <td onClick={companyEditable ? onChange : undefined} data-name="Active">
                                    {localCompany.active ? (
                                        <MdCheckBox className="actionIcon" />
                                    ) : (
                                        <MdCheckBoxOutlineBlank className="actionIcon" />
                                    )}
                                </td>
                            </tr>
                            <tr>
                                <th className="label">Billable</th>
                                <td onClick={companyEditable ? onChange : undefined} data-name="IsBillable">
                                    {localCompany.isBillable ? (
                                        <MdCheckBox className="actionIcon" />
                                    ) : (
                                        <MdCheckBoxOutlineBlank className="actionIcon" />
                                    )}
                                </td>
                            </tr>
                            <tr>
                                <th className="label">Departments Enabled</th>
                                <td onClick={companyEditable ? onChange : undefined} data-name="DepartmentsEnabled">
                                    {localCompany.departmentsEnabled ? (
                                        <MdCheckBox className="actionIcon" />
                                    ) : (
                                        <MdCheckBoxOutlineBlank className="actionIcon" />
                                    )}
                                </td>
                            </tr>
                            {canManageBilling && (
                                <tr>
                                    <th className="label">Billing email</th>
                                    <td>
                                        <input
                                            value={localCompany.billingEmail || ""}
                                            name="BillingEmail"
                                            type="email"
                                            className="companyAliasInput"
                                            disabled={billingSettingsSaving}
                                            onBlur={onBillingEmailBlur}
                                            onChange={(e) => updateLocalCompany({ ...localCompany, billingEmail: e.target.value })}
                                        />
                                    </td>
                                </tr>
                            )}
                            <tr>
                                <th className="label">Default User Role</th>
                                <td>
                                    <select
                                        value={localCompany.defaultUserRoleId || 0}
                                        name="DefaultUserRoleId"
                                        disabled={!companyEditable}
                                        onChange={onChange}
                                    >
                                        <option value="0"> None </option>
                                        {localCompany.roles
                                            ? localCompany.roles.map((role, i) => {
                                                return (
                                                    <option
                                                        key={"role" + (role.key !== undefined ? role.key : i)}
                                                        value={role.key}
                                                    >
                                                        {role.value}
                                                    </option>
                                                );
                                            })
                                            : null}
                                    </select>
                                </td>
                            </tr>
                            <tr>
                                <th className="label">Timed-out Status</th>
                                <td>
                                    <select
                                        value={localCompany.timedOutStatusId || 0}
                                        name="TimedOutStatusId"
                                        disabled={!companyEditable}
                                        onChange={onChange}
                                    >
                                        {localCompany && localCompany.statuses
                                            ? localCompany.statuses.map((s) => {
                                                return (
                                                    <option
                                                        key={"status" + (s.ctsId)}
                                                        value={s.ctsId}
                                                    >
                                                        {s.display}
                                                    </option>
                                                );
                                            })
                                            : null}
                                    </select>
                                </td>
                            </tr>
                            <tr>
                                <th className="label">Sub Entity Name</th>
                                <td>
                                    <input
                                        value={localCompany.subEntityTypeName || ""}
                                        name="SubEntityTypeName"
                                        type="text"
                                        readOnly={!companyEditable}
                                        onChange={onChange}
                                        className="subEntityTypeInput"
                                    />
                                </td>
                            </tr>
                            <tr>
                                <th
                                    className="label"
                                    onClick={() => setIsImageModalOpen(true)}
                                >
                                    Company Logo
                                </th>
                                <td>
                                    {localCompany.imageId && localCompany.imageId > 0 ? (
                                        <img
                                            className="logo company"
                                            src={companyImageUrl}
                                            alt=""
                                            style={{ maxWidth: "300px", maxHeight: "300px", objectFit: "contain" }}
                                            onClick={() => setIsImageModalOpen(true)}
                                        />

                                    ) : (
                                        <FaPlusSquare
                                            className="actionIcon plusIcon"
                                            title="Click to Change"
                                            onClick={() => setIsImageModalOpen(true)}
                                        />
                                    )}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </Modal>

            <ImagePickerModal
                isOpen={isImageModalOpen}
                onClose={() => {
                    setIsImageModalOpen(false);
                    getCompanyAsync();
                }}
                companyId={localCompany.id}
                updateSpinner={updateSpinner}
            />

            <StringInputModal
                isOpen={showDuplicateModal}
                onClose={() => setShowDuplicateModal(false)}
                onSubmit={handleDuplicateSubmit}
                title="Duplicate Company"
                label="New Company Name:"
                buttonText="Duplicate Company"
                placeholder="Enter new company name..."
                required={true}
                initialValue=""
                validation={(name) => {
                    if (name.trim().length < 2) {
                        return "Company name must be at least 2 characters long";
                    }
                    return null;
                }}
            />

            <ConfirmModal
                isOpen={showDeleteConfirm}
                onConfirm={handleDeleteConfirmed}
                onCancel={() => setShowDeleteConfirm(false)}
                title="Delete Company"
                message={`Are you sure you want to delete company \"${localCompany?.name || "this company"}\"?`}
                confirmText="Yes, Delete Company"
                dangerText="This will permanently remove the company and all associated data."
                showSecondStep={true}
                secondStepMessage={`Delete ${localCompany?.name || "this company"} permanently?`}
                secondStepWarning="This company will be permanently removed from the system."
                secondStepDetail="This includes users, sites, tests, statuses, roles, and related configuration. This action cannot be undone."
            />

            <StringInputModal
                isOpen={showDeletePasswordModal}
                onClose={handleDeletePasswordCancel}
                onSubmit={handleDeletePasswordSubmit}
                title="Confirm Admin Password"
                label="Enter the universal admin password to delete this company in production:"
                buttonText="Delete Company"
                placeholder="Admin password"
                required={true}
                inputType="password"
            />
        </>
    );
}


